import Link from '@components/common/Link/Link'
import AnimatedCheckmark from '@components/icons/AnimatedCheckmark/AnimatedCheckmark'
import EcomButtonGroup from '@components/ui/EcomButtonGroup/EcomButtonGroup'
import Location from '@components/ui/Icons/Location/Location'
import ShoppingCart from '@components/ui/Icons/ShoppingCart'
import LineClamp from '@components/ui/LineClampV2'
import RetailerButton from '@components/ui/RetailerButton/RetailerButton'
import { UmbracoLink } from '@customTypes/umbracoLink'

import { formatPriceV2 } from '@lib/format-price'
import { generateMediaUrlV2 } from '@lib/utils'
import {
  selectAlpha2Code,
  selectChannel,
  selectCountry,
  selectEcommerceEnabled,
  selectLabels,
  selectRetailerUrl,
} from '@redux/globalSlice'
import {
  addItem,
  removeItem,
  selectAlreadyInWishList,
} from '@redux/wishlistSlice'
import clsx from 'clsx'
import React, {
  FC,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'
import BuyBanner from '../ProductBuyBanner/product-buy-banner'
import SwatchGroup from '../SwatchGroup/SwatchGroup'
import TextSwatchGroup from '../TextSwatchGroup/TextSwatchGroup'
import s from './ProductSidebar.module.scss'

import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Button from '@components/ui/ButtonV2/Button'
import { useCartAddItem } from '@features/cart/api/cart-add-line-items'
import { getTrackingProductId } from '@features/products/utils'
import { getStockAvailability } from '@lib/get-stock-availability'
import {
  ProductAttributeType,
  ProductDetailFragment,
  ProductDetailVariantFragment,
} from '@lib/graphql/graphql'
import { openModalView } from '@redux/modalSlice'
import { useAppDispatch } from '@redux/store'
import { mapEdgesToItems } from '@utils/map'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'
import { AttributeGroup, getStockAvailabilityText } from '../helpersv2'

export type Specification = {
  label: string
  value: string
}

export interface ProductSidebarProps {
  productName: string
  productSecondaryName: string
  attributeGroups: AttributeGroup[]
  purchaseDisabled?: boolean
  productModuleInView?: boolean
  designerLink?: UmbracoLink
  configuratorLink?: UmbracoLink | null
  configuratorLinkAttribute?: string | null
  retailerTags?: string[] | null
  setBackInStockModalOpen?: (open: boolean) => void
  metadata?: {
    __typename?: 'KeyValuePairOfStringAndString'
    key: string
    value: string
  }[]
  productV2: ProductDetailFragment
  selectedVariantV2: ProductDetailVariantFragment
  productRef: MutableRefObject<HTMLFormElement>
}

const ProductSidebar: FC<ProductSidebarProps> = forwardRef(
  (
    {
      productName,
      productSecondaryName,
      attributeGroups,
      purchaseDisabled,
      productModuleInView,
      designerLink,
      configuratorLink,
      configuratorLinkAttribute,
      retailerTags,
      metadata,
      setBackInStockModalOpen,
      productV2,
      selectedVariantV2,
      productRef,
    },
    ref,
  ) => {
    const retailerUrl = useSelector(selectRetailerUrl)
    const alreadyInWishlist = useSelector(
      selectAlreadyInWishList(selectedVariantV2.id),
    )
    const ecommerceEnabled = useSelector(selectEcommerceEnabled)
    const alpha2Code = useSelector(selectAlpha2Code)

    const labels = useSelector(selectLabels)

    const country = useSelector(selectCountry)
    const channel = useSelector(selectChannel)
    const [loadingAddToCart, setLoadingAddToCart] = useState(false)

    const [showBuyBanner, setShowBuyBanner] = useState(false)

    const dispatch = useAppDispatch()

    const { mutate } = useCartAddItem()

    const handleAddToCart = () => {
      setLoadingAddToCart(true)
      setTimeout(() => {
        setLoadingAddToCart(false)
        mutate({
          variantId: selectedVariantV2.id,
          metadata: [
            {
              key: 'TRACKING_PRODUCT_ID',
              value:
                getTrackingProductId(productV2.id, productV2.metadata) ?? '',
            },
          ],
        })
      }, 1500)
    }

    const handleAddToWishlist = useCallback(() => {
      alreadyInWishlist
        ? dispatch(removeItem(selectedVariantV2.id))
        : dispatch(addItem(selectedVariantV2.id))
    }, [selectedVariantV2.sku, dispatch, alreadyInWishlist])

    const handleDeliveryUSP = useMemo(
      () =>
        getStockAvailabilityText({
          channel: channel,
          stockState: getStockAvailability(channel, selectedVariantV2.metadata),
        }),
      [selectedVariantV2, alpha2Code],
    )

    useEffect(() => {
      const handleScroll = () => {
        const scrollPosition = window.scrollY + window.innerHeight
        const currentViewportTop = window.scrollY

        if (productRef.current) {
          const rect = productRef.current.getBoundingClientRect()
          const productPosition = {
            top: rect.top + window.scrollY,
            bottom: rect.bottom + window.scrollY,
          }

          // Show banner when scrolled past product AND not scrolled above it
          const shouldShow =
            scrollPosition > productPosition.bottom && // Scrolled past bottom
            currentViewportTop > productPosition.top // Not above product top

          setShowBuyBanner(shouldShow)
        }
      }

      // Initial check
      handleScroll()

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
      <div className={s.root}>
        <div className={clsx(s['header'], s['padded'])}>
          <span className={s['designed-by']}>
            <Link link={designerLink}>
              By <span className={s['bold']}>{productV2.vendor}</span>
            </Link>
          </span>
          <h1 className={s['name']}>
            <span
              className={clsx(s['product-name'], {
                [s.large]: productName.length <= 20,
                [s.small]: productName.length > 20,
              })}
            >
              {productName}
            </span>
            <span className={s['product-secondary-name']}>
              {productSecondaryName}
            </span>
          </h1>
          <span className={s['price-wrapper']}>
            {selectedVariantV2.price?.discountedPrice && (
              <span className={s['discounted-price']}>
                {formatPriceV2(
                  alpha2Code,
                  selectedVariantV2.price?.discountedPrice.value,
                  2,
                )}
              </span>
            )}
            <span
              className={clsx(s['price'], {
                [s.strikethrough]: !!selectedVariantV2.price?.discountedPrice,
              })}
            >
              {formatPriceV2(alpha2Code, selectedVariantV2.price?.value)}
            </span>
            <p>
              {selectedVariantV2.price?.value.currencyCode === 'USD' ? (
                <span className={s['priceTaxModeLabel']}>excl. tax</span>
              ) : (
                <span className={s['priceTaxModeLabel']}>incl. VAT</span>
              )}
            </p>
          </span>
        </div>
        {productV2.description && (
          <div className={clsx(s['product-description'], s['padded'])}>
            <p className={s['product-description']}>
              <LineClamp
                lines={3}
                fontSize={19}
                text={productV2.description}
              ></LineClamp>
            </p>
          </div>
        )}

        <div className={s['seperator']} />
        <div className={s['content']}>
          {attributeGroups.map((group, index) => {
            const includeSeperator = index !== 0
            if (group.type === ProductAttributeType.Swatch) {
              return (
                <React.Fragment key={index}>
                  {includeSeperator && <div className={s['seperator']} />}
                  <SwatchGroup
                    className={s['padded']}
                    groupName={group.groupName}
                    swatches={group.options}
                  />
                </React.Fragment>
              )
            }
            if (group.type == ProductAttributeType.Text) {
              return (
                <React.Fragment key={index}>
                  {includeSeperator && <div className={s['seperator']} />}
                  <TextSwatchGroup
                    className={s['padded']}
                    groupName={group.groupName}
                    swatches={group.options}
                    configuratorLink={
                      configuratorLink &&
                      group.groupName === configuratorLinkAttribute
                        ? configuratorLink
                        : undefined
                    }
                  />
                </React.Fragment>
              )
            }

            return <></>
          })}
        </div>
        <div className={clsx(s['footer'], s['padded'])}>
          <span
            className={clsx(s['free-shipping-container'], {
              [s['with-discount']]: selectedVariantV2.price?.discountedPrice,
            })}
          >
            <div className={s['price-flex']}>
              <div>
                <span className={clsx(s['price-wrapper'])}>
                  {selectedVariantV2.price?.discountedPrice && (
                    <span className={s['discounted-price']}>
                      {formatPriceV2(
                        alpha2Code,
                        selectedVariantV2.price?.discountedPrice.value,
                        2,
                      )}
                    </span>
                  )}
                  <span
                    className={clsx(s['price'], {
                      [s.strikethrough]:
                        !!selectedVariantV2.price?.discountedPrice,
                    })}
                  >
                    {formatPriceV2(alpha2Code, selectedVariantV2.price?.value)}
                  </span>
                </span>
              </div>
              {ecommerceEnabled && (
                <div className={clsx(s.desktop, s.priceTaxModeLabel)}>
                  {selectedVariantV2.price?.value.currencyCode === 'USD' ? (
                    <>excl. tax</>
                  ) : (
                    <>incl. VAT</>
                  )}
                </div>
              )}
            </div>

            {!ecommerceEnabled && (
              <div className={clsx('hideOnTablet', s['buying-online'])}>
                {country.code.toLowerCase() === 'int' ? (
                  <TrustedHtml
                    html={labels.ecomNotAvailableInROWText.replace(
                      '{country}',
                      country.name,
                    )}
                  ></TrustedHtml>
                ) : (
                  <TrustedHtml
                    html={labels.ecomNotInCountryText.replace(
                      '{country}',
                      country.name,
                    )}
                  ></TrustedHtml>
                )}
                <Button
                  as={'a'}
                  size="no-padding"
                  variant="underline"
                  onClick={() => dispatch(openModalView('MARKET_SELECTION'))}
                  className={'!mt-2 w-fit'}
                >
                  {labels.changeCountryText}
                </Button>
              </div>
            )}
            {ecommerceEnabled && !purchaseDisabled && (
              <span
                className={clsx(s['free-shipping'], {
                  [s['disabled']]: purchaseDisabled,
                })}
              >
                <span className={s['free-shipping-icon']}>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="7.5"
                      cy="7.5"
                      r="7.5"
                      fill={handleDeliveryUSP?.circleColor}
                    />
                    <path d="M4 7.5L7 10L10.5 5" stroke="white" />
                  </svg>
                </span>
                <span>
                  <span className={s['bold']}>
                    {handleDeliveryUSP?.mainText}
                  </span>
                  <span>
                    {'  '}
                    {handleDeliveryUSP?.secondaryText}
                  </span>
                </span>
              </span>
            )}
          </span>
          {ecommerceEnabled && (
            <EcomButtonGroup
              className={s['ecom-btn-group']}
              onClickAddToCart={handleAddToCart}
              onClickAddToWishlist={handleAddToWishlist}
              disabled={purchaseDisabled}
              loadingAddToCart={loadingAddToCart}
              addedToWishList={alreadyInWishlist}
              metadata={metadata}
              setBackInStockModalOpen={setBackInStockModalOpen}
            />
          )}

          <RetailerButton
            retailerTags={retailerTags}
            className={s['retailer-btn']}
          />

          {!ecommerceEnabled && (
            <div className={clsx('my-4 showOnTablet', s['buying-online'])}>
              {country.code.toLowerCase() === 'int' ? (
                <TrustedHtml
                  html={labels.ecomNotAvailableInROWText.replace(
                    '{country}',
                    country.name,
                  )}
                ></TrustedHtml>
              ) : (
                <TrustedHtml
                  html={labels.ecomNotInCountryText.replace(
                    '{country}',
                    country.name,
                  )}
                ></TrustedHtml>
              )}
              <Button
                as={'a'}
                size="no-padding"
                variant="underline"
                onClick={() => dispatch(openModalView('MARKET_SELECTION'))}
                className={'!mt-2 w-fit'}
              >
                {labels.changeCountryText}
              </Button>
            </div>
          )}
          {/* <Accordion items={[{ title: "SPECIFICATIONS", children: <Specificaitons specificaitons={specifications} /> }, { title: "EXPLORE UPHOLSTERY", children: <></>, type: 'link', href: "https://gubi.com/upholstery", external: true }]}></Accordion> */}
        </div>
        <BuyBanner
          visible={showBuyBanner && !productModuleInView && ecommerceEnabled}
        >
          <div className={s['buy-banner-container']}>
            <div className={s['buy-banner-variant']}>
              <span className={s['buy-banner-variant-image-container']}>
                <img
                  className={s['buy-banner-variant-image']}
                  src={generateMediaUrlV2(
                    mapEdgesToItems(selectedVariantV2.media).find((x) => x)
                      ?.src,
                  )?.replace('{size}', '128')}
                />
              </span>
              <span className={s['buy-banner-details']}>
                <span className={s['buy-banner-price-wrapper']}>
                  {selectedVariantV2.price?.discountedPrice && (
                    <span className={s['buy-banner-discounted-price']}>
                      {formatPriceV2(
                        alpha2Code,
                        selectedVariantV2.price?.discountedPrice.value,
                        2,
                      )}
                    </span>
                  )}
                  <span className={s['buy-banner-variant-price']}>
                    {formatPriceV2(alpha2Code, selectedVariantV2.price?.value)}
                  </span>
                </span>

                <span className={s['buy-banner-variant-name']}>
                  {productName}
                </span>
              </span>
            </div>
            <div className={s['buy-banner-button-container']}>
              <Link
                link={{ url: retailerUrl }}
                className={s['find-retailer-link']}
              >
                <Location />
              </Link>
              {ecommerceEnabled && !purchaseDisabled && (
                <button
                  type="button"
                  className={s['buy-banner-add-to-cart-btn']}
                  onClick={handleAddToCart}
                >
                  {loadingAddToCart ? (
                    <div className={s['checkmark']}>
                      <div className={s['checkmark-inner']}>
                        <AnimatedCheckmark />
                      </div>
                      &nbsp;
                    </div>
                  ) : (
                    <>
                      <ShoppingCart width={24} height={24} />
                      <span className={s['buy-banner-add-to-cart-badge']}>
                        <svg
                          width="15"
                          height="31"
                          viewBox="0 0 15 31"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="7.5" cy="15.7" r="7" fill="white" />
                          <path
                            d="M10.92 15.972H8.064V18.982H7.049V15.972H4.207V15.027H7.049V12.038H8.064V15.027H10.92V15.972Z"
                            fill="black"
                          />
                        </svg>
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </BuyBanner>
      </div>
    )
  },
)

ProductSidebar.displayName = 'ProductSidebar'
export default ProductSidebar
